import type { LanguageType } from '@/types/language';

/**
 * ### HomeHeroHeadlineId
 *
 * Identifies one of the press-kit headline candidates. The set is closed because
 * every candidate has been reviewed as public messaging; a headline that is not
 * on this list has not been through that review.
 */
export type HomeHeroHeadlineId =
  | 'proven-on-line'
  | 'working-on-line'
  | 'sound-ai-of-manufacturing'
  | 'listening-around-the-clock';

/**
 * ### HOME_HERO_HEADLINE_IDS
 *
 * The headline candidates in the order the press kit lists them, with the
 * framework Core Message first. Kept as an ordered list so a review discussion
 * can refer to "candidate 2" and mean the same line the press kit does.
 */
export const HOME_HERO_HEADLINE_IDS = [
  'proven-on-line',
  'working-on-line',
  'sound-ai-of-manufacturing',
  'listening-around-the-clock',
] as const satisfies ReadonlyArray<HomeHeroHeadlineId>;

/**
 * ### ACTIVE_HOME_HERO_HEADLINE_ID
 *
 * The single switch that decides which candidate the claim section renders.
 * Swapping headlines is meant to be this one-line edit, so no component may
 * pick a headline by index or by matching on its text.
 */
export const ACTIVE_HOME_HERO_HEADLINE_ID: HomeHeroHeadlineId =
  'proven-on-line';

/**
 * ### HomeHeroMetricId
 *
 * Identifies one of the three evidence metrics shown under the claim. The set
 * is fixed: adding a fourth is a messaging decision, not a layout tweak.
 */
export type HomeHeroMetricId = 'scale' | 'speed-volume' | 'accuracy-trust';

/**
 * ### HomeHeroMetric
 *
 * One evidence metric as label, value, and the sentence that explains it.
 * Every figure here is a public performance claim, so an edit needs the same
 * review as press material.
 */
export type HomeHeroMetric = {
  /**
   * ### id
   *
   * Stable identifier used as a React key and as the analytics item id, so it
   * must not change when the wording of the metric changes.
   */
  id: HomeHeroMetricId;

  /**
   * ### label
   *
   * Short heading for the metric. Carries no sentence punctuation because it is
   * a label rather than a sentence.
   */
  label: string;

  /**
   * ### value
   *
   * The figure itself, formatted for display. Kept separate from the
   * description so it can be typeset larger without slicing the sentence.
   */
  value: string;

  /**
   * ### description
   *
   * The press-kit sentence that gives the figure its meaning. Written to stand
   * on its own when quoted without the label or the value beside it.
   */
  description: string;

  /**
   * ### note
   *
   * Optional supporting sentence the press kit sets beside the figure: either
   * the figure restated in human terms, or a second piece of evidence that is
   * read together with it. A note measured over a narrower range has to name
   * that range in its own text, because it does not inherit the scope of the
   * value above it. Absent where the press kit offers none, and an absent note
   * renders nothing rather than an empty line.
   */
  note?: string;

  /**
   * ### definition
   *
   * How the figure is defined — what exactly is being counted. Rendered as a
   * footnote under the metric when present.
   *
   * Left unset until the press kit carries confirmed wording: a definition
   * written here would become a public claim about how a published number is
   * measured.
   */
  definition?: string;

  /**
   * ### scope
   *
   * The measurement range the figure was taken over: which sites, which
   * inspection type, which period. Rendered as a footnote beside the
   * definition when present, and left unset on the same grounds.
   */
  scope?: string;
};

/**
 * ### HomeHeroNarrative
 *
 * The press kit's Sub-copy narrative: the objection a manufacturing reader
 * arrives with, and the lines that answer it. Held apart from
 * {@link HomeHeroCopy.subcopy} because the subcopy has to stay a single
 * sentence for the claim and its metrics to read as one block, while this
 * block is read after them.
 */
export type HomeHeroNarrative = {
  /**
   * ### lead
   *
   * The objection itself, kept in the press kit's question form. It is the
   * only line here written to be read before the answer, so it renders as the
   * section's own statement rather than as a list item.
   */
  lead: string;

  /**
   * ### points
   *
   * The answers to that objection, in press-kit order. Ordered rather than a
   * set: the evidence comes first, the reason second, the breadth last.
   */
  points: ReadonlyArray<HomeHeroNarrativePoint>;
};

/**
 * ### HomeHeroNarrativePointId
 *
 * Identifies one answer in the narrative. Fixed so the icon drawn beside a
 * point is chosen by identity, never by position or by matching its text.
 */
export type HomeHeroNarrativePointId = 'analyses' | 'expansion' | 'coverage';

/**
 * ### HomeHeroNarrativePoint
 *
 * One answer to the objection: the press-kit sentence, and the single figure
 * from the same press kit that the sentence rests on, with the words that say
 * what that figure counts.
 */
export type HomeHeroNarrativePoint = {
  /**
   * ### id
   *
   * Stable identifier, used as the React key and to pick the icon.
   */
  id: HomeHeroNarrativePointId;

  /**
   * ### figure
   *
   * The number as displayed, kept short enough to set large. A figure whose
   * press-kit scope is narrower than the sentence beside it has to carry
   * that scope in {@link figureCaption}, because the figure is read first.
   */
  figure: string;

  /**
   * ### figureCaption
   *
   * What the figure counts, in a few words under it.
   */
  figureCaption: string;

  /**
   * ### text
   *
   * The press-kit sentence itself.
   */
  text: string;
};

/**
 * ### HomeHeroCopy
 *
 * Every press-kit string the home page opens with, for one language: the
 * claim section under the hero and the narrative after it. The hero's own
 * brand line and the CTA labels are not here, because they are not press-kit
 * messaging: the brand line is fixed across languages and the labels are UI
 * strings kept in the translation catalogue.
 */
export type HomeHeroCopy = {
  /**
   * ### headlines
   *
   * All reviewed candidates, keyed by identifier so the active one resolves
   * without an index lookup that could silently drift.
   */
  headlines: Record<HomeHeroHeadlineId, string>;

  /**
   * ### subcopy
   *
   * The single paragraph under the headline. One sentence by design; a second
   * sentence pushes the evidence metrics off the first screen.
   */
  subcopy: string;

  /**
   * ### narrative
   *
   * The press kit narrative that follows the claim section, answering the
   * objection the subcopy leaves open.
   */
  narrative: HomeHeroNarrative;

  /**
   * ### metrics
   *
   * The three evidence metrics in display order.
   */
  metrics: ReadonlyArray<HomeHeroMetric>;
};

/**
 * ### HOME_HERO_COPY_BY_LOCALE
 *
 * Home opening messaging per language. Korean is the press-kit original and
 * is reproduced verbatim; English is a direct translation of that same copy,
 * so it must never carry a figure, a range, or a claim the Korean does not.
 *
 * @see {@link ACTIVE_HOME_HERO_HEADLINE_ID} Chooses which headline renders
 */
export const HOME_HERO_COPY_BY_LOCALE: Record<LanguageType, HomeHeroCopy> = {
  ko: {
    headlines: {
      'proven-on-line': '글로벌 양산 라인에서 검증된 소리 AI는 디플리뿐입니다',
      'working-on-line':
        '글로벌 양산 라인에서 작동하는 소리 AI는 디플리뿐입니다',
      'sound-ai-of-manufacturing': "글로벌 제조현장의 '소리 AI, 디플리'",
      'listening-around-the-clock': '24시간 전 세계 제조현장의 소리를 듣습니다',
    },
    subcopy:
      '완성차 조립라인부터 EOL 검사까지, 전 세계 30+개의 도입 사이트에서 실시간으로 AI 판정이 이루어지고 있습니다',
    narrative: {
      lead: '테스트실에서는 다 됩니다. 문제는 소음 가득한 양산 라인입니다',
      points: [
        {
          id: 'analyses',
          figure: '1억 회+',
          figureCaption: '누적 양산 현장 분석 횟수',
          text: '누적 1억 회를 넘어선 양산 현장 분석 실적이 그 증거입니다',
        },
        {
          id: 'expansion',
          figure: '100%',
          figureCaption:
            '추가 라인 증설을 논의 중인 도입 고객사 (모터 이음검사 분야 한정)',
          text: '시끄러운 공장 환경에서도 진성 불량을 놓치지 않고, 다수의 도입 기업이 추가 라인 증설을 논의합니다. 디플리는 데모가 아니라 양산 현장에서 그 답을 ROI로 증명합니다',
        },
        {
          id: 'coverage',
          figure: '30+',
          figureCaption: '전 세계 도입 사이트',
          text: '완성차 조립라인, 커넥터 체결 감지, EOL 검사까지 고객들은 다양하게 디플리 AI를 적용하고 있습니다',
        },
      ],
    },
    metrics: [
      {
        id: 'scale',
        label: 'Scale',
        value: '누적 52만 시간 (20TB)',
        description:
          '24시간 멈추지 않는 양산 라인에서, 디플리가 직접 수집한 누적 52만 시간(20TB)의 음향 데이터',
        note: '숙련된 검사원 178명이 1년 내내, 하루 8시간씩 쉬지 않고 들어야 하는 소리를, 디플리는 이미 다 들었습니다',
      },
      {
        id: 'speed-volume',
        label: 'Speed & Volume',
        value: '매 시간 6,000개 · 매달 430만 개',
        description:
          '완성차 조립라인부터 EOL 공정까지, 전 세계 30+ 사이트에서 매 시간 6,000개 제품, 매달 430만 개를 100% 인라인(In-line)으로 판정합니다',
      },
      {
        id: 'accuracy-trust',
        label: 'Accuracy & Trust',
        value: '99.98%',
        description:
          '누적 1억 회 분석 속에서 진성 불량을 놓치지 않는 99.98%의 정확도. 초고소음 공장 환경에서도 SNR 1.77dB 수준의 미세한 신호 차이를 검출해 불량을 판정합니다',
        note: '도입 고객사 100%가 추가 라인 증설을 논의하고 있습니다 (모터 이음검사 분야 한정)',
      },
    ],
  },
  en: {
    headlines: {
      'proven-on-line':
        'Deeply is the only proven Sound AI on global production lines.',
      'working-on-line':
        'Deeply is the only sound AI running on global production lines',
      'sound-ai-of-manufacturing':
        'Deeply, the sound AI of global manufacturing sites',
      'listening-around-the-clock':
        'Listening to manufacturing sites around the world, 24 hours a day',
    },
    subcopy:
      'From vehicle assembly lines to EOL inspection, AI judgments are being made in real time at 30+ deployed sites worldwide',
    narrative: {
      lead: 'Defects are easy to detect in a test lab. The real challenge is detecting them on a noisy production line.',
      points: [
        {
          id: 'analyses',
          figure: '100M+',
          figureCaption: 'cumulative analyses on production floors',
          text: 'Proof: Over 100 million analyses on production floors. Proven in the real world.',
        },
        {
          id: 'expansion',
          figure: '100%',
          figureCaption:
            'of adopting customers discussing further lines (motor abnormal-noise inspection)',
          text: 'It does not miss a true defect even in a noisy plant, and many adopters are discussing further lines. Deeply proves the answer as ROI on the production floor, not in a demo',
        },
        {
          id: 'coverage',
          figure: '30+',
          figureCaption: 'deployed sites worldwide',
          text: 'From vehicle assembly lines to connector fastening detection and EOL inspection, manufacturers are applying Deeply AI across a wide range of manufacturing processes.',
        },
      ],
    },
    metrics: [
      {
        id: 'scale',
        label: 'Scale',
        value: '520,000 cumulative hours (20TB)',
        description:
          '520,000 hours of acoustic data (20 TB), collected directly by Deeply from production lines running 24/7.',
        note: 'What would take 178 experienced inspectors an entire year to hear, Deeply has already heard.',
      },
      {
        id: 'speed-volume',
        label: 'Speed & Volume',
        value: '6,000 products per hour \u00b7 4.3 million per month',
        description:
          'From vehicle assembly lines to EOL processes, 30+ sites worldwide judge 6,000 products an hour and 4.3 million a month, 100% in-line',
      },
      {
        id: 'accuracy-trust',
        label: 'Accuracy & Trust',
        value: '99.98%',
        description:
          '99.98% accuracy, proven through more than 100 million analyses. Even in extremely noisy production environments, Deeply detects signal differences as subtle as 1.77 dB SNR — so genuine defects don’t go unnoticed.',
        note: '100% of existing customers using motor abnormal-noise inspection are exploring expansion to additional production lines.',
      },
    ],
  },
};

/**
 * ### resolveHomeHeroHeadline
 *
 * Reads the headline the claim section should render for a language. Total
 * by construction: every candidate exists in every language, so there is no
 * missing-headline case for the caller to handle.
 *
 * @param locale Language the page is rendered in
 * @returns The active headline text for that language
 */
export function resolveHomeHeroHeadline(locale: LanguageType): string {
  return HOME_HERO_COPY_BY_LOCALE[locale].headlines[
    ACTIVE_HOME_HERO_HEADLINE_ID
  ];
}
